'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useUser } from '@clerk/clerk-react';
import { driver, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { CircleNotchIcon, SignOutIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useItems } from '@/hooks/queries/useItems';
import { useProfile } from '@/hooks/queries/useProfile';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { usePathname, useRouter } from '@/lib/router';
import { cn } from '@/lib/utils';
import { useFeatureFlags, useFeatureFlagsSettled } from '@/lib/telemetry';
import { PROBLEM_REPORT_FLAG } from '@/lib/problemReport';
import {
  buildPlatformProductTourSteps,
  hasUsableConnection,
  isSubscriptionAccessBlocked,
  platformOnboardingStorageKey,
  shouldRunProductTour,
  type PlatformProductTourStep,
} from './platformOnboarding';

// The gate after VER-388: first-run identity (CPF, WhatsApp, consent) moved into
// /onboarding's Validação step, so this component keeps only what genuinely guards
// the dashboard — the subscription-blocked overlay, the redirect that sends
// unfinished users to /onboarding, and the driver.js product tour. The @onboardjs
// step machine and its three modal steps are gone.

const LEGACY_COACHMARK_KEYS = [
  'vertice.bottomnav.coachmark.v1.done',
  'vertice.bottomnav.coachmark.v1.step',
];

function readTutorialCompleted(userId: string): boolean {
  try {
    return window.localStorage.getItem(platformOnboardingStorageKey(userId)) === '1';
  } catch {
    return false;
  }
}

function writeTutorialCompleted(userId: string): void {
  try {
    window.localStorage.setItem(platformOnboardingStorageKey(userId), '1');
  } catch {
    // Storage is best-effort; required setup still comes from the API.
  }
}

function clearLegacyCoachmarkStorage(): void {
  try {
    for (const key of LEGACY_COACHMARK_KEYS) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Ignore unavailable storage.
  }
}

export function PlatformOnboardingGate({
  children,
  onLogout,
}: {
  children: ReactNode;
  onLogout: () => void;
}) {
  const { user } = useUser();
  const pathname = usePathname();
  const router = useRouter();
  const userId = user?.id ?? null;

  const { data: profile, error: profileError, mutate: refetchProfile } = useProfile();
  const { data: subscription, error: subscriptionError } = useSubscription();
  const { data: items, error: itemsError, mutate: refetchItems } = useItems();

  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  useEffect(() => {
    if (!userId) return;
    clearLegacyCoachmarkStorage();
    setTutorialCompleted(readTutorialCompleted(userId));
  }, [userId]);

  const completeTutorial = useCallback(() => {
    if (userId) writeTutorialCompleted(userId);
    setTutorialCompleted(true);
  }, [userId]);

  const profileFailed = profileError !== undefined && profile === undefined;
  const itemsFailed = itemsError !== undefined && items === undefined;
  const retryFirstRunData = useCallback(() => {
    if (profileFailed) void refetchProfile();
    if (itemsFailed) void refetchItems();
  }, [itemsFailed, profileFailed, refetchItems, refetchProfile]);

  const subscriptionBlocked = isSubscriptionAccessBlocked(subscription);
  // The subscription read must SETTLE (load or fail) before the gate acts, or
  // an expired subscriber whose profile resolves first is redirected to
  // /onboarding — outside this gate's pay overlay. A FAILED read then fails
  // open on purpose: entitlements are enforced server-side (calls 402), and the
  // trial-copy e2e pins that an unreachable subscriptions/me degrades quietly —
  // blocking the whole dashboard on that hiccup would be the worse failure.
  const subscriptionSettled = subscription !== undefined || subscriptionError !== undefined;

  // The three reasons a user belongs on /onboarding, all enforced here because
  // the modals that used to enforce the first one are gone:
  // 1. identity incomplete — no CPF, or an unverified WhatsApp where the
  //    environment requires it (the whatsAppVerificationRequired clause keeps
  //    staging, which runs with the flag off, out of a redirect loop);
  // 2. no usable connection (the strict predicate — blocked items don't count);
  // 3. entered the flow and never finished. Strictly `=== true`: users predating
  //    the flow have no onboarding row and must never be swept in (VER-368).
  const validationMissing =
    profile !== undefined
    && (!profile.hasCpf
      || (profile.whatsAppVerificationRequired && !profile.hasWhatsAppUserId));
  const connectionMissing = items !== undefined && !hasUsableConnection(items);
  const onboardingUnfinished =
    profile?.onboardingStarted === true && !profile.onboardingCompletedAt;

  const needsFirstRunSetup =
    profile !== undefined
    && subscriptionSettled
    && !subscriptionBlocked
    && (validationMissing || connectionMissing || onboardingUnfinished)
    // /profile is the billing surface — the upgrade CTAs (onboarding's capped
    // step, the 402 dialog) send users here, and the subscription-blocked
    // overlay below already treats it as always-reachable. Redirecting away
    // would make upgrading impossible for exactly the users asked to upgrade.
    && pathname !== '/profile';

  useEffect(() => {
    if (!needsFirstRunSetup) return;
    router.replace('/onboarding');
  }, [needsFirstRunSetup, router]);

  // The driver freezes its step list at construction, so mounting before the
  // first flag resolution would drop the flag-appended problem-report step for
  // every fresh tour. Bounded: flags settle on first resolution or the grace.
  const flagsSettled = useFeatureFlagsSettled();

  const tourReady =
    flagsSettled
    && profile !== undefined
    && subscriptionSettled
    && items !== undefined
    && !needsFirstRunSetup
    && !onboardingUnfinished
    && shouldRunProductTour({
      whatsAppVerificationRequired: profile.whatsAppVerificationRequired,
      hasWhatsAppUserId: profile.hasWhatsAppUserId,
      hasCpf: profile.hasCpf,
      hasUsableConnection: hasUsableConnection(items),
      tutorialCompleted,
      subscriptionBlocked,
    });

  if (!userId) return <>{children}</>;

  // The subscription overlay outranks the data-failure one: for a blocked
  // subscriber the only actionable path is upgrading, and a failed /items read
  // must not bury that behind a generic retry.
  if (subscriptionBlocked && pathname !== '/profile') {
    return (
      <>
        {children}
        <SubscriptionBlockedOverlay
          onOpenProfile={() => router.replace('/profile')}
          onLogout={onLogout}
        />
      </>
    );
  }

  // Items get the same treatment as the profile: with /items failed and no
  // data, connectionMissing is unknowable and the gate would silently fail
  // open (a connectionless user browsing an empty dashboard). The subscription
  // is deliberately NOT here — see subscriptionSettled above. /profile stays
  // reachable like everywhere else in this gate: it is the billing surface,
  // has its own load-error states, and covering it would lock a paying user
  // out of paying.
  if ((profileFailed || itemsFailed) && pathname !== '/profile') {
    return (
      <>
        {children}
        <BlockingStatusOverlay
          title="Não foi possível preparar seu onboarding"
          message="Tente novamente em alguns instantes."
          onRetry={retryFirstRunData}
          onLogout={onLogout}
        />
      </>
    );
  }

  // Hold the dashboard behind a status overlay for the frame or two the replace
  // takes, so first-run users never glimpse an empty painel before /onboarding
  // takes over.
  if (needsFirstRunSetup) {
    return (
      <>
        {children}
        <BlockingStatusOverlay
          title="Vamos configurar sua conta"
          message="Levando você para a configuração inicial."
          onLogout={onLogout}
        />
      </>
    );
  }

  return (
    <>
      {children}
      {tourReady && <ProductTour onComplete={completeTutorial} />}
    </>
  );
}

function SubscriptionBlockedOverlay({
  onOpenProfile,
  onLogout,
}: {
  onOpenProfile: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-neutral-950/45 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-blocked-title"
        className="w-full max-w-sm rounded-2xl border border-border-subtle bg-surface p-5 shadow-2xl"
      >
        <div className="min-w-0">
          <p className="text-sm/6 font-medium text-brand-700 sm:text-xs/5">
            Assinatura necessária
          </p>
          <h2 id="subscription-blocked-title" className="mt-1 text-lg font-semibold text-text-primary">
            Sua avaliação terminou
          </h2>
          <p className="mt-1 text-base/7 text-text-secondary sm:text-sm/6">
            Assine Starter ou Pro no Perfil para continuar usando a plataforma.
          </p>
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onLogout}>
            <SignOutIcon className="size-4" />
            Sair
          </Button>
          <Button type="button" onClick={onOpenProfile}>
            Ver planos
          </Button>
        </div>
      </div>
    </div>
  );
}

function BlockingStatusOverlay({
  title,
  message,
  onRetry,
  onLogout,
}: {
  title: string;
  message?: string;
  onRetry?: () => void;
  onLogout?: () => void;
}) {
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-neutral-950/45 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-onboarding-status-title"
        className="w-full max-w-sm rounded-2xl border border-border-subtle bg-surface p-5 shadow-2xl"
      >
        <div className="flex items-start gap-3">
          <CircleNotchIcon className={cn('mt-0.5 size-5 shrink-0 text-brand-700', !onRetry && 'animate-spin')} />
          <div className="min-w-0">
            <h2 id="platform-onboarding-status-title" className="text-lg font-semibold text-text-primary">
              {title}
            </h2>
            <p className="mt-1 text-base/7 text-text-secondary sm:text-sm/6">
              {message ?? 'Só um instante enquanto conferimos CPF e conexões.'}
            </p>
          </div>
        </div>
        {(onRetry || onLogout) && (
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {onLogout && (
              <Button type="button" variant="secondary" onClick={onLogout}>
                <SignOutIcon className="size-4" />
                Sair
              </Button>
            )}
            {onRetry && (
              <Button type="button" onClick={onRetry}>
                Tentar novamente
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductTour({ onComplete }: { onComplete: () => void }) {
  const router = useRouter();
  const completingRef = useRef(false);
  const movingRef = useRef(false);
  const skipCompletionOnDestroyRef = useRef(false);
  const tourRef = useRef<Driver | null>(null);

  // The problem-report tour step only exists when its flag is on. The flag can still resolve
  // (false -> true) after this component mounts for a returning user, so the step list is frozen
  // in a ref the tour effect reads once at construction — a mid-tour flag flip updates the ref
  // without tearing down and restarting the running tour.
  const problemReportEnabled = useFeatureFlags(PROBLEM_REPORT_FLAG);
  const tourSteps = useMemo(
    () => buildPlatformProductTourSteps(problemReportEnabled),
    [problemReportEnabled],
  );
  const tourStepsRef = useRef(tourSteps);
  useEffect(() => {
    tourStepsRef.current = tourSteps;
  }, [tourSteps]);

  const completeTour = useCallback(() => {
    if (completingRef.current) return;
    completingRef.current = true;
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    let cancelled = false;
    const isTourMobile = window.matchMedia('(max-width: 767px)').matches;
    const steps = tourStepsRef.current;

    const moveToTourIndex = async (tour: Driver, index: number) => {
      if (movingRef.current || completingRef.current) return;

      if (index < 0) {
        return;
      }

      if (index >= steps.length) {
        tour.destroy();
        return;
      }

      movingRef.current = true;
      await prepareProductTourStep(steps[index], isTourMobile, router);
      movingRef.current = false;

      if (!cancelled && tour.isActive() && !completingRef.current) {
        tour.moveTo(index);
      }
    };

    const productTour = driver({
      steps: buildProductTourDriveSteps(isTourMobile, steps),
      animate: true,
      allowClose: true,
      allowKeyboardControl: true,
      disableActiveInteraction: true,
      overlayColor: '#18181b',
      overlayOpacity: 0.28,
      overlayClickBehavior: 'close',
      popoverClass: 'vertice-product-tour-popover',
      popoverOffset: isTourMobile ? 12 : 16,
      showProgress: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: 'Próximo',
      prevBtnText: 'Voltar',
      doneBtnText: 'Concluir',
      stagePadding: isTourMobile ? 6 : 8,
      stageRadius: isTourMobile ? 12 : 14,
      smoothScroll: true,
      onPopoverRender: (popover) => {
        popover.title.setAttribute('role', 'heading');
        popover.title.setAttribute('aria-level', '2');
        popover.closeButton.setAttribute('aria-label', 'Pular tutorial');
        popover.closeButton.setAttribute('title', 'Pular tutorial');
      },
      onNextClick: (_element, _step, { driver: activeTour }) => {
        const activeIndex = activeTour.getActiveIndex() ?? 0;
        void moveToTourIndex(activeTour, activeIndex + 1);
      },
      onPrevClick: (_element, _step, { driver: activeTour }) => {
        const activeIndex = activeTour.getActiveIndex() ?? 0;
        void moveToTourIndex(activeTour, activeIndex - 1);
      },
      onCloseClick: (_element, _step, { driver: activeTour }) => {
        activeTour.destroy();
      },
      onDestroyed: () => {
        if (skipCompletionOnDestroyRef.current) {
          skipCompletionOnDestroyRef.current = false;
          return;
        }

        completeTour();
      },
    });

    tourRef.current = productTour;

    void prepareProductTourStep(steps[0], isTourMobile, router).then(() => {
      if (!cancelled && !completingRef.current) {
        productTour.drive();
      }
    });

    return () => {
      cancelled = true;
      const activeTour = tourRef.current;
      tourRef.current = null;

      if (activeTour?.isActive() && !completingRef.current) {
        skipCompletionOnDestroyRef.current = true;
        activeTour.destroy();
      }
    };
  }, [completeTour, router]);

  return null;
}

function buildProductTourDriveSteps(
  isMobile: boolean,
  steps: PlatformProductTourStep[],
): DriveStep[] {
  return steps.map((step) => ({
    element: () => findProductTourTarget(step, isMobile) as Element,
    popover: {
      title: step.title,
      description: step.body,
      side: isMobile ? step.mobileSide ?? step.side : step.side,
      align: isMobile ? step.mobileAlign ?? step.align : step.align,
    },
  }));
}

async function prepareProductTourStep(
  step: PlatformProductTourStep,
  isMobile: boolean,
  router: ReturnType<typeof useRouter>,
) {
  if (step.route && window.location.pathname !== step.route) {
    router.push(step.route);
    await waitForProductTourReady(() => window.location.pathname === step.route, 1800);
  }

  await waitForProductTourReady(() => Boolean(findProductTourTarget(step, isMobile)), 2200);

  const target = findProductTourTarget(step, isMobile);
  target?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });

  await new Promise((resolve) => window.requestAnimationFrame(resolve));
}

function findProductTourTarget(step: PlatformProductTourStep, isMobile: boolean): Element | null {
  const selector = isMobile
    ? step.mobileSelector ?? step.selector
    : step.selector;

  return findFirstMatchingElement(selector);
}

function findFirstMatchingElement(selector: string): Element | null {
  for (const candidate of selector.split(',').map((part) => part.trim()).filter(Boolean)) {
    const element = document.querySelector(candidate);
    if (element) return element;
  }

  return null;
}

function waitForProductTourReady(predicate: () => boolean, timeoutMs: number) {
  return new Promise<void>((resolve) => {
    const startedAt = performance.now();

    const tick = () => {
      if (predicate() || performance.now() - startedAt >= timeoutMs) {
        resolve();
        return;
      }

      window.setTimeout(tick, 50);
    };

    tick();
  });
}
